import { ArrowCircleDown as ArrowCircleDownIcon } from '@mui/icons-material';
import {
  Collapse,
  Grid,
  LinearProgress,
} from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import { Icon } from 'assets/Icon.js';
import type {
  FileRejection,
  FileWithPath,
} from 'react-dropzone';

import {
  FileContent,
  FilesWrapper,
  FileText,
  StyledIconButton,
  Text,
  UploadBox,
  Wrapper,
} from './FileUploadInput.styled.js';
import type {
  FileInfoType,
  FileUploadInputProps,
} from './FileUploadInput.types.js';
import { formatErrorMessage } from './FileUploadInput.utils.js';

const FILE_ITEM_HEIGHT = 37;

const FileInfo = ({
  name,
  errors = [],
  removeFile,
  openFile,
  showDownload,
}: FileInfoType) => {
  return (
    <FileContent data-testid="uploaded-file">
      <Grid container>
        <Grid size={{ xs: showDownload ? 5 : 6 }} container>
          <FileText
            noWrap
            maxWidth={156}
            sx={{
              color: theme => theme.palette.text.secondary,
            }}
          >
            {name ?? ''}
          </FileText>
        </Grid>
        <Grid size={{ xs: 5 }}>
          {errors?.length > 0 && (
            <FileText color="error">
              {formatErrorMessage(errors?.[0]?.code)}
            </FileText>
          )}
        </Grid>
        {showDownload
          ? (
              <Grid size={{ xs: 1 }} container>
                <StyledIconButton onClick={openFile} aria-label="Download file">
                  <SvgIcon
                    component={ArrowCircleDownIcon}
                    inheritViewBox
                    sx={{
                      fontSize: '21px',
                      fill: ({ palette }) => palette.primary.dark,
                    }}
                  />
                </StyledIconButton>
              </Grid>
            )
          : null}
        <Grid size={{ xs: 1 }} container>
          <StyledIconButton onClick={removeFile} aria-label="Remove file">
            <Icon icon="outlineDeleteSvg" sx={{ height: 21 }} />
          </StyledIconButton>
        </Grid>
      </Grid>
    </FileContent>
  );
};

export const FileUploadInput = ({
  files,
  getRootProps,
  getInputProps,
  rejectedFiles,
  removeFile,
  removeRejectedFile,
  wrapperProps,
  openFile,
  showDownload = false,
  filesToDisplay = 3,
  uploadInProgress,
}: FileUploadInputProps) => {
  return (
    <Wrapper {...wrapperProps}>
      <UploadBox {...getRootProps()}>
        <input {...getInputProps()} />
        <Icon sx={{ height: 21 }} icon="uploadSvg" />
        <Text>
          <span>
            <strong>Drag and drop</strong>
            {' '}
            or browse to choose a file
          </span>
        </Text>
      </UploadBox>
      {uploadInProgress ? <LinearProgress sx={{ width: '100%' }} /> : null}
      <Collapse
        style={{ width: '100%' }}
        in={files.length > 0 || rejectedFiles.length > 0}
      >
        <FilesWrapper style={{ maxHeight: filesToDisplay * FILE_ITEM_HEIGHT }} data-testid="uploaded-files">
          {files.map((file: string, index: number) => {
            return (
              <FileInfo
                key={`file-${file}-${index}`}
                name={file}
                removeFile={() => removeFile(index)}
                openFile={() => openFile(index)}
                showDownload={showDownload}
              />
            );
          })}
          {rejectedFiles.map((rejected: FileRejection, index: number) => {
            const file = rejected?.file as FileWithPath;
            return (
              <FileInfo
                key={`rejected-file-${file?.path}-${index}`}
                name={file?.path ?? ''}
                errors={rejected.errors}
                removeFile={() => removeRejectedFile(index)}
                openFile={() => openFile(index)}
                showDownload={showDownload}
              />
            );
          })}
        </FilesWrapper>
      </Collapse>
    </Wrapper>
  );
};
